import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchBotEncuestaList, fetchBotEncuestaReview } from '../api/botEncuesta';
import BotEncuestaSurveyList from '../components/botEncuesta/BotEncuestaSurveyList';
import { useAuth } from '../context/AuthContext';
import { CwListSearch } from '../components/conversaciones/CwUiParts';
import '../styles/gestion-derivacion-premium.css';
import '../styles/bot-encuesta-review-premium.css';
import '../styles/bot-encuesta-survey-list.css';

const REVIEW_ENCUESTA_KEY = 'ber_selected_encuesta_id';

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (_) {
    return String(value);
  }
}

function formatValor(tipo, valor) {
  if (valor === undefined || valor === null || valor === '') return '—';
  if (tipo === 'rating') return `${valor}/5`;
  return String(valor);
}

function resolvePatientFields(row) {
  const nombre = String(row?.nombre || row?.meta?.nombre || row?.meta?.name || '').trim();
  const dni = String(row?.dni || row?.meta?.dni || row?.meta?.documento || '').trim();
  return {
    nombre: nombre || '—',
    dni: dni || '—'
  };
}

function rowSearchBlob(row, preguntas) {
  const patient = resolvePatientFields(row);
  const parts = [patient.nombre, patient.dni, formatDate(row.created_at)];
  (preguntas || []).forEach((q) => {
    const item = (row.items || []).find(
      (i) => Number(i.pregunta_id) === Number(q.pregunta_id)
        || String(i.titulo || '').trim() === String(q.titulo || '').trim()
    );
    if (item) parts.push(formatValor(q.tipo, item.valor));
  });
  return parts.join(' ').toLowerCase();
}

function shortQuestionLabel(q) {
  if (q.tipo === 'rating') return 'Facilidad';
  if (q.tipo === 'si_no') return '¿Entendió?';
  if (q.tipo === 'texto') return 'Comentario';
  return `Opción ${q.orden}`;
}

function findAnswerItem(row, q) {
  return (row.items || []).find(
    (i) => Number(i.pregunta_id) === Number(q.pregunta_id)
      || String(i.titulo || '').trim() === String(q.titulo || '').trim()
  );
}

function AnswerValue({ tipo, valor }) {
  if (valor === undefined || valor === null || valor === '') {
    return <span className="ber-ans-empty">Sin respuesta</span>;
  }
  if (tipo === 'rating') {
    return (
      <span className="ber-ans-pill ber-ans-pill--rating">
        <strong>{valor}</strong>
        <span>/ 5</span>
      </span>
    );
  }
  if (tipo === 'si_no') {
    const yes = String(valor).toLowerCase() === 'sí' || String(valor).toLowerCase() === 'si';
    return (
      <span className={`ber-ans-pill ber-ans-pill--yn${yes ? ' ber-ans-pill--yes' : ' ber-ans-pill--no'}`}>
        {String(valor)}
      </span>
    );
  }
  if (tipo === 'texto') {
    return <p className="ber-ans-text">{String(valor)}</p>;
  }
  return <span className="ber-ans-pill">{String(valor)}</span>;
}

function truncateSearchTerm(term, max = 36) {
  const t = String(term || '').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

function truncateComment(text, max = 88) {
  const t = String(text || '').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

function SearchNoResults({ query, onClear }) {
  const term = String(query || '').trim();
  return (
    <div className="ber-search-empty" role="status">
      <span className="ber-search-empty__icon" aria-hidden="true">
        🔎
      </span>
      <p className="ber-search-empty__title">Sin coincidencias</p>
      {term ? (
        <p className="ber-search-empty__term" title={term}>
          {truncateSearchTerm(term)}
        </p>
      ) : null}
      <p className="ber-search-empty__hint">
        Probá con nombre, DNI, fecha o texto de alguna respuesta.
      </p>
      <button type="button" className="ber-search-empty__action" onClick={onClear}>
        Limpiar búsqueda
      </button>
    </div>
  );
}

function ResponseCard({ row, preguntas }) {
  const patient = resolvePatientFields(row);
  const hasPatient = patient.nombre !== '—' || patient.dni !== '—';

  return (
    <article className="ber-response-card">
      <header className="ber-response-card__head">
        <div className="ber-response-card__meta">
          <time className="ber-response-card__date">{formatDate(row.created_at)}</time>
          {hasPatient ? (
            <div className="ber-response-card__patient">
              {patient.nombre !== '—' ? (
                <span className="ber-response-card__name">{patient.nombre}</span>
              ) : null}
              {patient.dni !== '—' ? (
                <span className="ber-response-card__dni">DNI {patient.dni}</span>
              ) : null}
            </div>
          ) : (
            <span className="ber-response-card__anon">Respuesta anónima</span>
          )}
        </div>
      </header>
      <div className="ber-response-card__answers">
        {preguntas.map((q) => {
          const item = findAnswerItem(row, q);
          return (
            <div key={q.pregunta_id} className={`ber-ans-row ber-ans-row--${q.tipo}`}>
              <span className="ber-ans-label">
                <span className="ber-ans-num">{q.orden}</span>
                {shortQuestionLabel(q)}
              </span>
              <div className="ber-ans-value">
                <AnswerValue tipo={q.tipo} valor={item?.valor} />
              </div>
            </div>
          );
        })}
      </div>
    </article>
  );
}

function RatingBars({ distribucion = {}, total = 0 }) {
  const entries = [5, 4, 3, 2, 1];
  return (
    <div className="ber-rating-bars">
      {entries.map((n) => {
        const count = Number(distribucion[n] || 0);
        const pct = total ? Math.round((count / total) * 100) : 0;
        return (
          <div key={n} className="ber-rating-bar-row">
            <span className="ber-rating-bar-label">{n}</span>
            <div className="ber-rating-bar-track">
              <span className="ber-rating-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="ber-rating-bar-count">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

function QuestionStatCard({ stat, totalRespuestas = 0 }) {
  if (!stat) return null;

  if (stat.tipo === 'rating') {
    return (
      <article className="ber-q-card">
        <h3 className="ber-q-title" title={stat.titulo}>{stat.titulo}</h3>
        <div className="ber-q-kpi">
          <span className="ber-q-kpi-value">{stat.promedio ?? '—'}</span>
          <span className="ber-q-kpi-label">promedio / 5</span>
        </div>
        <p className="ber-q-meta">{stat.total} respuesta{stat.total === 1 ? '' : 's'}</p>
        <RatingBars distribucion={stat.distribucion} total={stat.total} />
      </article>
    );
  }

  if (stat.tipo === 'si_no') {
    const total = stat.total || 0;
    const siPct = total ? Math.round((stat.si / total) * 100) : 0;
    const noPct = total ? Math.round((stat.no / total) * 100) : 0;
    return (
      <article className="ber-q-card">
        <h3 className="ber-q-title" title={stat.titulo}>{stat.titulo}</h3>
        <div className="ber-yn-stats">
          <div className="ber-yn-stat ber-yn-stat--yes">
            <span className="ber-yn-stat-value">{stat.si}</span>
            <span className="ber-yn-stat-label">Sí ({siPct}%)</span>
          </div>
          <div className="ber-yn-stat ber-yn-stat--no">
            <span className="ber-yn-stat-value">{stat.no}</span>
            <span className="ber-yn-stat-label">No ({noPct}%)</span>
          </div>
        </div>
        <div className="ber-yn-bar">
          <span className="ber-yn-bar-yes" style={{ width: `${siPct}%` }} />
          <span className="ber-yn-bar-no" style={{ width: `${noPct}%` }} />
        </div>
      </article>
    );
  }

  if (stat.tipo === 'texto') {
    const textos = Array.isArray(stat.textos) ? stat.textos.map((t) => String(t).trim()).filter(Boolean) : [];
    const comentarios = textos.length;
    const pctComentarios = totalRespuestas ? Math.round((comentarios / totalRespuestas) * 100) : 0;

    return (
      <article className="ber-q-card ber-q-card--texto">
        <h3 className="ber-q-title" title={stat.titulo}>{stat.titulo}</h3>
        <div className="ber-q-kpi">
          <span className="ber-q-kpi-value">{comentarios}</span>
          <span className="ber-q-kpi-label">comentario{comentarios === 1 ? '' : 's'}</span>
        </div>
        <p className="ber-q-meta">
          {totalRespuestas
            ? `${pctComentarios}% dejó comentario · ${totalRespuestas} respuesta${totalRespuestas === 1 ? '' : 's'}`
            : 'Sin respuestas todavía'}
        </p>
        {textos.length ? (
          <ul className="ber-texto-feed">
            {textos.slice(0, 4).map((t, i) => (
              <li key={`txt-${i}`} title={t}>
                {truncateComment(t)}
              </li>
            ))}
          </ul>
        ) : (
          <p className="ber-empty-hint">Todavía no hay comentarios de texto.</p>
        )}
      </article>
    );
  }

  const opciones = stat.opciones || {};
  const entries = Object.entries(opciones);
  return (
    <article className="ber-q-card">
      <h3 className="ber-q-title" title={stat.titulo}>{stat.titulo}</h3>
      <ul className="ber-opt-list">
        {entries.map(([label, count]) => (
          <li key={label}>
            <span>{label}</span>
            <strong>{count}</strong>
          </li>
        ))}
      </ul>
    </article>
  );
}

function hasPermission(user, code) {
  return Boolean((user?.permissions || []).includes(code));
}

export default function BotEncuestaReview() {
  const { user } = useAuth();
  const canSeeBuilder = hasPermission(user, 'ver_encuesta_bot');
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState('');
  const [encuestaList, setEncuestaList] = useState([]);
  const [selectedEncuestaId, setSelectedEncuestaId] = useState(null);
  const [encuesta, setEncuesta] = useState(null);
  const [stats, setStats] = useState(null);
  const [respuestas, setRespuestas] = useState([]);
  const [tableSearch, setTableSearch] = useState('');

  const loadReview = useCallback(async (encuestaId) => {
    setLoading(true);
    setError('');
    try {
      const json = await fetchBotEncuestaReview(encuestaId);
      setEncuesta(json?.data || null);
      setStats(json?.stats || null);
      setRespuestas(Array.isArray(json?.respuestas) ? json.respuestas : []);
      if (json?.data?.id) {
        setSelectedEncuestaId(json.data.id);
        try {
          sessionStorage.setItem(REVIEW_ENCUESTA_KEY, String(json.data.id));
        } catch (_) { /* ignore */ }
      }
    } catch (err) {
      setError(err.message || 'No se pudo cargar el review');
      setEncuesta(null);
      setStats(null);
      setRespuestas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setListLoading(true);
    setError('');
    try {
      const listJson = await fetchBotEncuestaList();
      const list = Array.isArray(listJson?.data) ? listJson.data : [];
      setEncuestaList(list);

      if (!list.length) {
        setSelectedEncuestaId(null);
        setEncuesta(null);
        setStats(null);
        setRespuestas([]);
        return;
      }

      let preferredId = selectedEncuestaId;
      if (!preferredId) {
        try {
          const stored = Number(sessionStorage.getItem(REVIEW_ENCUESTA_KEY));
          if (Number.isFinite(stored) && stored > 0) preferredId = stored;
        } catch (_) { /* ignore */ }
      }

      const exists = list.some((item) => Number(item.id) === Number(preferredId));
      const targetId = exists ? preferredId : list[0].id;
      await loadReview(targetId);
    } catch (err) {
      setError(err.message || 'No se pudo cargar las encuestas');
      setEncuestaList([]);
      setEncuesta(null);
      setStats(null);
      setRespuestas([]);
    } finally {
      setListLoading(false);
    }
  }, [loadReview, selectedEncuestaId]);

  useEffect(() => {
    load();
  }, []);

  const handleSelectEncuesta = async (id) => {
    if (Number(selectedEncuestaId) === Number(id)) return;
    setTableSearch('');
    await loadReview(id);
  };

  const ratingStat = useMemo(
    () => (stats?.por_pregunta || []).find((s) => s.tipo === 'rating'),
    [stats]
  );

  const siNoStat = useMemo(
    () => (stats?.por_pregunta || []).find((s) => s.tipo === 'si_no'),
    [stats]
  );

  const total = stats?.total_respuestas || 0;
  const preguntas = stats?.por_pregunta || [];

  const questionStats = useMemo(
    () => [...(stats?.por_pregunta || [])].sort((a, b) => Number(a.orden) - Number(b.orden)),
    [stats]
  );

  const filteredRespuestas = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return respuestas;
    return respuestas.filter((row) => rowSearchBlob(row, preguntas).includes(q));
  }, [respuestas, preguntas, tableSearch]);

  return (
    <section className="crm-gd-page crm-pbody ber-page">
      <header className="ber-head crm-gd-toolbar">
        <div className="ber-head-copy">
          <p className="ber-eyebrow">Grupo Bienestar</p>
          <h1 className="ber-title">Review de encuestas</h1>
          <p className="ber-sub">
            {encuesta
              ? `Resultados de «${encuesta.titulo}»`
              : 'Creá y publicá una encuesta desde el generador.'}
          </p>
        </div>
        <div className="ber-head-actions">
          {encuesta ? (
            <span className={`ber-status ber-status--${encuesta.estado}`}>
              {encuesta.estado === 'publicada' ? 'Publicada' : 'Borrador'}
            </span>
          ) : null}
          {canSeeBuilder ? (
            <Link to="/encuesta-bot" className="ber-btn ber-btn--primary">
              Generador
            </Link>
          ) : null}
          <button type="button" className="ber-btn ber-btn--ghost" onClick={load} disabled={loading}>
            {loading ? 'Actualizando…' : 'Actualizar'}
          </button>
        </div>
      </header>

      {error ? <div className="ber-alert ber-alert--error" role="alert">{error}</div> : null}

      <BotEncuestaSurveyList
        surveys={encuestaList}
        selectedId={selectedEncuestaId}
        onSelect={handleSelectEncuesta}
        loading={listLoading}
      />

      {loading && !encuesta ? (
        <div className="ber-loading" role="status">
          <span className="ber-loading-spinner" aria-hidden />
          Cargando respuestas…
        </div>
      ) : null}

      {!loading && !listLoading && !encuestaList.length ? (
        <div className="ber-empty">
          <p>No hay encuestas configuradas todavía.</p>
          {canSeeBuilder ? (
            <Link to="/encuesta-bot" className="ber-btn ber-btn--primary">Ir al generador</Link>
          ) : null}
        </div>
      ) : null}

      {encuesta ? (
        <>
          <div className="ber-kpis">
            <article className="ber-kpi">
              <span className="ber-kpi-label">Total respuestas</span>
              <strong className="ber-kpi-value">{total}</strong>
            </article>
            <article className="ber-kpi">
              <span className="ber-kpi-label">Facilidad promedio</span>
              <strong className="ber-kpi-value">
                {ratingStat?.promedio != null ? `${ratingStat.promedio}/5` : '—'}
              </strong>
            </article>
            <article className="ber-kpi">
              <span className="ber-kpi-label">Bot entendió (Sí)</span>
              <strong className="ber-kpi-value">
                {siNoStat?.total ? `${siNoStat.pct_si ?? 0}%` : '—'}
              </strong>
            </article>
            <article className="ber-kpi">
              <span className="ber-kpi-label">Última actualización</span>
              <strong className="ber-kpi-value ber-kpi-value--sm">
                {formatDate(encuesta.updated_at)}
              </strong>
            </article>
          </div>

          <section className="ber-stats-section">
            <div className="ber-stats-head">
              <div>
                <h2>Resumen por pregunta</h2>
                <p className="ber-stats-sub">Distribución de las {questionStats.length} preguntas de la encuesta</p>
              </div>
              <span className="ber-stats-count">{questionStats.length}</span>
            </div>
            <div className="ber-stats-grid">
              {questionStats.map((stat) => (
                <QuestionStatCard key={stat.pregunta_id} stat={stat} totalRespuestas={total} />
              ))}
            </div>
          </section>

          <section className="ber-responses-section">
            <div className="ber-responses-head">
              <div>
                <h2>Respuestas individuales</h2>
                <p className="ber-responses-sub">Detalle de cada envío del paciente</p>
              </div>
              <span className="ber-responses-count">
                {tableSearch.trim()
                  ? `${filteredRespuestas.length} de ${respuestas.length}`
                  : respuestas.length}
              </span>
            </div>

            {respuestas.length ? (
              <div className="ber-responses-search crm-gd-toolbar__search">
                <CwListSearch
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  placeholder="Buscar por nombre, DNI, fecha o respuesta…"
                />
              </div>
            ) : null}

            {!respuestas.length ? (
              <p className="ber-empty-hint">Cuando los pacientes respondan, vas a ver el detalle acá.</p>
            ) : !filteredRespuestas.length ? (
              <SearchNoResults
                query={tableSearch}
                onClear={() => setTableSearch('')}
              />
            ) : (
              <div className="ber-responses-list">
                {filteredRespuestas.map((row) => (
                  <ResponseCard key={row.id} row={row} preguntas={preguntas} />
                ))}
              </div>
            )}
          </section>
        </>
      ) : null}
    </section>
  );
}
